import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Eye, Plus, RefreshCw } from "lucide-react";
import { useAuth } from "../../../../lib/auth";
import useOrders from "./useOrders";
import useStorefronts from "@/features/vendor/common/storefronts/useStorefronts";
import * as repository from "@/data/orderRepository";
import { formatINR, formatDateTime } from "../../../../lib/utils";
import StatusBadge from "@/shared/components/feedback/StatusBadge";
import StoreFilter from "@/features/vendor/common/analytics/components/StoreFilter";
import Modal from "@/shared/components/feedback/Modal";
import { getTableState } from "@/domain/restaurantTables";
import { canCancelOrder, canTransitionOrder } from "@/domain/restaurantOrders";
import { buildTableUrl, createQrSvgDataUrl } from "@/domain/publicStoreUrls";
import RestaurantPOS from "@/features/vendor/restaurant/pos/RestaurantPOS";
import { getTableServiceRequests, updateTableServiceRequestStatus } from "@/data/tableServiceRequestRepository";
import { normalizeVendorCapabilities } from "@/domain/vendorCapabilities";
import ServiceRequestsPanel from "./components/ServiceRequestsPanel";
import { APP_CONFIG } from "@/config/appConfig";
import useClientPagination from "@/shared/hooks/useClientPagination";
import Pagination from "@/shared/components/navigation/Pagination";
import { api, isMockApi } from "@/lib/api";

export default function OrdersPage() {
  const { RESTAURANT_ORDER_STATUSES } = repository;
  const { vendor, employee, isOwner } = useAuth();
  const { allowed } = useStorefronts();
  const [storeFilter, setStoreFilter] = useState("all");
  const { orders, setStatus, cancel, closeTable, appendItems, reload: reloadOrders, getDetail } = useOrders({ storefrontId: storeFilter });
  const [q, setQ] = useState("");
  const [status, setStatus_] = useState("all");
  const [location, setLocation] = useState("all");
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [requestTick, setRequestTick] = useState(0);
  const [serverRequests, setServerRequests] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [tab, setTab] = useState("orders");
  const [additionId, setAdditionId] = useState("");
  const [additionSizeId, setAdditionSizeId] = useState("");
  const [draftItems, setDraftItems] = useState([]);
  const [savingDraft, setSavingDraft] = useState(false);
  const showTables = vendor.capabilities?.tableOrdering && ["table", "both"].includes(vendor.orderMode);
  const showRequests = showTables && normalizeVendorCapabilities(vendor.capabilities, vendor.businessType).tableServiceRequests;
  const allowedStorefrontIds = useMemo(() => new Set(allowed.map(({ id }) => id)), [allowed]);
  const scopedTables = useMemo(() => (vendor.tables || []).filter((table) => allowedStorefrontIds.has(table.storefrontId) && (storeFilter === "all" || table.storefrontId === storeFilter)), [vendor.tables, allowedStorefrontIds, storeFilter]);
  const scopedRequests = useMemo(() => (isMockApi ? getTableServiceRequests(vendor.id) : serverRequests).filter((request) => allowedStorefrontIds.has(request.storefrontId) && (storeFilter === "all" || request.storefrontId === storeFilter)), [vendor.id, allowedStorefrontIds, storeFilter, requestTick, serverRequests]);
  const activeRequestCount = scopedRequests.filter(({ status }) => status !== "completed").length;

  const reloadRequests = useCallback(async () => {
    if (isMockApi || !showRequests) return;
    setServerRequests(await api.listVendorServiceRequests({ storefrontId: storeFilter }));
  }, [showRequests, storeFilter]);

  const reload = useCallback(async () => {
    await Promise.all([reloadOrders(), reloadRequests()]);
  }, [reloadOrders, reloadRequests]);

  useEffect(() => {
    const refreshRequests = () => setRequestTick((value) => value + 1);
    const onVisibility = () => { if (document.visibilityState === "visible") refreshRequests(); };
    window.addEventListener("storage", refreshRequests);
    window.addEventListener("focus", refreshRequests);
    document.addEventListener("visibilitychange", onVisibility);
    return () => { window.removeEventListener("storage", refreshRequests); window.removeEventListener("focus", refreshRequests); document.removeEventListener("visibilitychange", onVisibility); };
  }, []);

  useEffect(() => {
    if (!isMockApi && showRequests) reloadRequests().catch(() => setServerRequests([]));
  }, [showRequests, requestTick, reloadRequests]);

  useEffect(() => { if (!showRequests && tab === "requests") setTab("orders"); }, [showRequests, tab]);

  useEffect(() => {
    if (location !== "all" && location !== "counter" && !scopedTables.some((table) => table.id === location)) setLocation("all");
  }, [location, scopedTables]);

  const filtered = orders.filter(
    (o) =>
      (status === "all" || o.status === status) &&
      (!showTables || location === "all" || (location === "counter" ? o.source !== "table" : o.source === "table" && o.tableId === location)) &&
      (q.trim() === "" ||
        o.code.toLowerCase().includes(q.toLowerCase()) ||
        o.customer?.name?.toLowerCase().includes(q.toLowerCase())),
  );
  const pagination = useClientPagination(filtered);
  const pagedOrders = pagination.pageItems;

  const setStatusFor = async (id, next) => {
    try {
      const updated = await setStatus(id, next);
      if (!updated) return toast.error("That status change is no longer valid. Refresh and try the next available action.");
      toast.success(`Marked as ${next}`);
      if (viewing?.id === id) setViewing(updated);
    } catch (error) {
      toast.error(error.message || "Unable to update this order.");
    }
  };
  const openOrder = async (order) => {
    try {
      const detail = await getDetail(order.id);
      if (detail) setViewing(detail);
    } catch (error) {
      toast.error(error.message || "Unable to load order details.");
    }
  };
  const stageItemForViewing = () => {
    const item = (vendor.items || []).find(({ id }) => id === additionId);
    if (!item) return toast.error("Choose an available menu item");
    const portion = (item.sizes || []).find((size) => size.id === additionSizeId && size.isAvailable !== false);
    if (item.sizes?.length && !portion) return toast.error("Choose an available portion");
    const lineId = `${item.id}:${portion?.id || "base"}`;
    setDraftItems((current) => { const existing = current.find((entry) => entry.lineId === lineId); return existing ? current.map((entry) => entry.lineId === lineId ? { ...entry, qty: entry.qty + 1 } : entry) : [...current, { id: item.id, lineId, menuItemSizeId: portion?.id || null, sizeLabel: portion?.label || null, name: item.name, price: portion?.price ?? item.price, qty: 1 }]; });
    setAdditionId("");
    setAdditionSizeId("");
  };
  const saveDraftItems = async () => {
    if (!draftItems.length || savingDraft) return;
    setSavingDraft(true);
    try {
      const updated = await appendItems(viewing.id, draftItems);
      if (!updated) return toast.error("Changes were not saved. This order may no longer accept items; your draft was kept.");
      setViewing(updated); setDraftItems([]); toast.success("Order changes saved");
    } catch (error) {
      toast.error(error.message || "Changes were not saved.");
    } finally {
      setSavingDraft(false);
    }
  };
  const cancelViewing = async () => {
    if (!window.confirm(`Cancel ${viewing.code}${viewing.tableLabel ? ` and mark ${viewing.tableLabel} ready to close` : ""}?`)) return;
    try {
      const result = await cancel(viewing.id);
      if (!result) return toast.error("This order can no longer be cancelled");
      setViewing(result); toast.success("Order cancelled");
    } catch (error) {
      toast.error(error.message || "This order can no longer be cancelled.");
    }
  };
  const completeViewingTable = async () => {
    try {
      const result = await closeTable(viewing.id);
      if (!result) return toast.error("Complete or cancel the order before closing the table");
      setViewing(null); toast.success("Table is now free");
    } catch (error) {
      toast.error(error.message || "Complete or cancel the order before closing the table.");
    }
  };
  const transitionRequest = async (id, next) => {
    try {
      if (isMockApi) {
        const updated = updateTableServiceRequestStatus(id, next, { vendorId: vendor.id, allowedStorefrontIds: [...allowedStorefrontIds] });
        if (!updated) return toast.error("That request can no longer be changed");
      } else {
        const updated = await api.updateVendorServiceRequestStatus(id, next);
        setServerRequests((current) => current.map((request) => request.id === id ? updated : request));
      }
      setRequestTick((value) => value + 1);
      toast.success(`Request ${next}`);
    } catch (error) {
      toast.error(error.message || "Unable to update this request.");
    }
  };

  if (vendor.businessType !== "restaurant") {
    return <div className="text-ink-secondary">Orders are only available for restaurant accounts.</div>;
  }

  return (
    <div className="space-y-5 sm:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-ink-muted">Live</div>
          <h1 className="mt-1 text-3xl font-display font-medium tracking-tight" data-testid="orders-title">Orders</h1>
          <p className="mt-1 text-ink-secondary">
            {isOwner
              ? "Manage every order across your stores."
              : `Orders for your ${allowed.length} assigned storefront${allowed.length === 1 ? "" : "s"}.`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2"><StoreFilter storefronts={allowed} value={storeFilter} onChange={(value) => { setStoreFilter(value); pagination.resetPage(); }} testid="orders-store-filter" /><button onClick={() => reload().catch((error) => toast.error(error.message || "Unable to refresh orders."))} className="btn-ghost min-h-[44px] w-fit !px-3 !py-2 inline-flex items-center gap-2 text-sm" data-testid="orders-refresh-button" aria-label="Refresh orders"><RefreshCw className="h-4 w-4" /> Refresh</button><button onClick={() => setNewOrderOpen(true)} className="btn-primary min-h-[44px] w-fit !px-3 !py-2 inline-flex items-center gap-2 text-sm" data-testid="new-order-link"><Plus className="h-4 w-4" /> New order</button></div>
      </div>

      {showRequests && activeRequestCount > 0 && <button onClick={() => setTab("requests")} className="card-surface w-full min-h-[52px] px-4 py-3 flex items-center justify-between gap-3 border-brand/40 text-left" data-testid="active-requests-banner"><span><strong>{activeRequestCount} active table request{activeRequestCount === 1 ? "" : "s"}</strong><span className="block text-xs text-ink-secondary">Open requests to respond</span></span><span className="text-brand font-medium">View</span></button>}

      <div role="tablist" aria-label="Orders workspace" className="flex gap-2 border-b border-line overflow-x-auto overscroll-x-contain pb-1"><button role="tab" aria-selected={tab === "orders"} onClick={() => setTab("orders")} className="min-h-[44px] shrink-0 px-4" data-testid="orders-tab">All Orders</button>{showTables && <button role="tab" aria-selected={tab === "tables"} onClick={() => setTab("tables")} className="min-h-[44px] shrink-0 px-4" data-testid="tables-tab">Tables</button>}{showRequests && <button role="tab" aria-selected={tab === "requests"} onClick={() => setTab("requests")} className="min-h-[44px] shrink-0 px-4 whitespace-nowrap font-medium" data-testid="requests-tab">Requests <span className="rounded-full bg-brand px-1.5 py-0.5 text-white" data-testid="requests-active-count">{activeRequestCount}</span></button>}</div>

      {tab === "requests" && showRequests ? <RequestsList requests={scopedRequests} onTransition={transitionRequest} /> : tab === "tables" && showTables ? <TableGrid vendor={vendor} tables={scopedTables} orders={orders} onOpen={(order) => { if (order) { openOrder(order); setTab("orders"); } }} /> : <>

      <div className="grid gap-2 sm:gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_180px_220px]">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); pagination.resetPage(); }}
            placeholder="Search by code or customer"
            className="input-field pl-10"
            data-testid="orders-search"
          />
        </div>
        <select
          value={status}
          onChange={(e) => { setStatus_(e.target.value); pagination.resetPage(); }}
          className="input-field sm:max-w-[180px]"
          data-testid="orders-status-filter"
        >
          <option value="all">All statuses</option>
          {RESTAURANT_ORDER_STATUSES.map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        {showTables && <label className="sr-only" htmlFor="orders-location-filter">Order location</label>}
        {showTables && <select id="orders-location-filter" value={location} onChange={(event) => { setLocation(event.target.value); pagination.resetPage(); }} className="input-field" data-testid="orders-location-filter"><option value="all">All order locations</option><option value="counter">Counter/direct</option>{scopedTables.map((table) => <option key={table.id} value={table.id}>{table.label}</option>)}</select>}
      </div>

      {filtered.length === 0 ? (
        <div className="card-surface p-8 sm:p-16 text-center">
          <div className="font-display text-lg">No orders match</div>
          <p className="mt-1 text-ink-secondary text-sm">Try changing filters or share your store link to get orders in.</p>
        </div>
      ) : (
        <>
        <ul className="md:hidden space-y-2.5" data-testid="orders-mobile-list">
          {pagedOrders.map((order) => (
            <MobileOrderCard key={order.id} order={order} onView={() => openOrder(order)} />
          ))}
        </ul>
        <div className="hidden md:block card-surface overflow-hidden" data-testid="orders-desktop-table">
          <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[11px] uppercase tracking-widest text-ink-muted border-b border-line">
            <div className="col-span-2">Order</div>
            <div className="col-span-3">Customer</div>
            <div className="col-span-3">Items</div>
            <div className="col-span-1">Total</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-right">Action</div>
          </div>
          <ul className="divide-y divide-line">
            {pagedOrders.map((o) => (
              <li key={o.id} className="px-4 sm:px-5 py-4 grid md:grid-cols-12 gap-3 items-center hover:bg-white/[0.02] transition-colors" data-testid={`order-row-${o.id}`}>
                <div className="md:col-span-2">
                  <div className="font-mono text-sm">{o.code}</div>
                  <div className="text-[11px] text-ink-muted">{formatDateTime(o.createdAt)}</div>
                  {o.source === "table" && <div className="mt-1 text-xs font-medium" data-testid={`order-table-${o.id}`}>{o.tableLabel || "Table unavailable"}</div>}
                </div>
                <div className="md:col-span-3">
                  <div className="text-sm truncate">{o.customer?.name}</div>
                  <div className="text-[11px] text-ink-muted truncate">{o.customer?.phone}</div>
                </div>
                <div className="md:col-span-3 text-sm text-ink-secondary truncate">
                  {o.items.length ? o.items.map((i) => `${i.name} ×${i.qty}`).join(", ") : `${o.itemCount || 0} item${o.itemCount === 1 ? "" : "s"}`}
                </div>
                <div className="md:col-span-1 font-mono text-sm">{formatINR(o.total)}</div>
                <div className="md:col-span-2">
                  <StatusBadge status={o.status} />
                </div>
                <div className="md:col-span-1 flex justify-start md:justify-end">
                  <button
                    onClick={() => openOrder(o)}
                    className="btn-ghost !py-1.5 !px-2.5 inline-flex items-center gap-1 text-xs"
                    data-testid={`view-order-${o.id}`}
                  >
                    <Eye className="h-3.5 w-3.5" /> View
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <Pagination {...pagination} onPageChange={pagination.setPage} onPageSizeChange={pagination.setPageSize} label="Orders" testId="orders-pagination" />
        </>
      )}

      <Modal open={newOrderOpen} onClose={() => setNewOrderOpen(false)} className="h-[92vh] sm:h-auto sm:!max-w-5xl overflow-x-hidden" labelledBy="new-order-modal-title"><div data-testid="new-order-modal" className="min-w-0"><h2 id="new-order-modal-title" className="mb-4 font-display text-2xl">New order</h2>{allowed[0] && <RestaurantPOS embedded storefront={(storeFilter !== "all" && allowed.find(({ id }) => id === storeFilter)) || allowed[0]} employee={employee} vendor={vendor} vendorId={vendor.id} onOrderCreated={reload} onClose={() => { reload(); setNewOrderOpen(false); }} />}</div></Modal>

      <Modal open={!!viewing} onClose={() => { setViewing(null); setDraftItems([]); setAdditionId(""); }} className="sm:max-w-xl">
        {viewing && (
          <div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs text-ink-muted font-mono">{viewing.code}</div>
                <h2 className="mt-1 font-display text-2xl font-medium">Order details</h2>
                <div className="mt-1 text-sm text-ink-secondary">{formatDateTime(viewing.createdAt)}</div>
              </div>
              <StatusBadge status={viewing.status} />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <InfoBlock label="Customer">{viewing.customer.name}</InfoBlock>
              <InfoBlock label="Phone">{viewing.customer.phone}</InfoBlock>
              {viewing.source === "table" && <InfoBlock label="Table">{viewing.tableLabel || "Table unavailable"}</InfoBlock>}
              <InfoBlock label="Delivery address" className="col-span-2">
                {viewing.customer.address || "—"}
              </InfoBlock>
              {viewing.notes && (
                <InfoBlock label="Notes" className="col-span-2">{viewing.notes}</InfoBlock>
              )}
            </div>

            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-ink-muted mb-2">Items</div>
              <ul className="rounded-lg border border-line divide-y divide-line">
                {viewing.items.map((i) => (
                  <li key={i.id} className="flex items-center justify-between px-4 py-3">
                    <div className="text-sm">{i.name} <span className="text-ink-muted">×{i.qty}</span></div>
                    <div className="font-mono text-sm">{formatINR(i.price * i.qty)}</div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 space-y-1.5 text-sm">
              <Row label="Subtotal" value={formatINR(viewing.subtotal)} />
              {viewing.taxDisplayed !== false && viewing.taxEnabled !== false && <Row label={`Tax (${viewing.taxRate ?? 5}%)`} value={formatINR(viewing.tax)} />}
              <Row label="Total" value={formatINR(viewing.total)} bold />
            </div>

            <div className="mt-6 pt-5 border-t border-line">
              <div className="text-xs uppercase tracking-widest text-ink-muted mb-3">Change status</div>
              <div className="flex flex-wrap gap-2" aria-label="Order status journey" data-testid="order-status-journey">
                {RESTAURANT_ORDER_STATUSES.map((s) => {
                  const isCurrent = viewing.status === s;
                  const isNext = !["completed", "cancelled"].includes(viewing.status) && canTransitionOrder(viewing.status, s);
                  const isCancellation = s === "cancelled" && canCancelOrder(viewing.status);
                  const enabled = isNext || isCancellation;
                  const journey = ["new", "preparing", "ready", "completed"];
                  const isPast = viewing.status !== "cancelled" && s !== "cancelled" && journey.indexOf(s) < journey.indexOf(viewing.status);
                  const explanation = isCurrent
                    ? `${s} is the current status`
                    : enabled
                      ? s === "cancelled" ? "Cancel this order" : `Move order to ${s}`
                      : viewing.status === "cancelled" || viewing.status === "completed"
                        ? "Terminal orders cannot be changed"
                        : isPast ? "Completed step; orders cannot move backwards" : "Complete the current step first";
                  const descriptionId = `order-status-description-${viewing.id}-${s}`;
                  return (
                  <React.Fragment key={s}>
                  <button
                    onClick={() => s === "cancelled" ? cancelViewing() : setStatusFor(viewing.id, s)}
                    disabled={!enabled}
                    aria-current={isCurrent ? "step" : undefined}
                    aria-describedby={descriptionId}
                    className={`min-h-[44px] px-3 py-2 rounded-full text-xs border transition-colors ${
                      isCurrent
                        ? "bg-brand-soft border-brand/50 text-ink-primary ring-1 ring-brand/30"
                        : enabled
                          ? "bg-bg-elevated border-line text-ink-primary hover:border-brand/50 focus-visible:ring-2 focus-visible:ring-brand"
                          : isPast
                            ? "bg-white/[0.03] border-line text-ink-secondary"
                            : "bg-bg-elevated border-line text-ink-muted opacity-60"
                    }`}
                    data-state={isCurrent ? "current" : isPast ? "past" : enabled ? "next" : "unavailable"}
                    data-testid={s === "cancelled" ? "cancel-order-button" : `change-status-${s}`}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                  <span id={descriptionId} className="sr-only">{explanation}</span>
                  </React.Fragment>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-ink-muted">Only the next step is available. Completed orders cannot be reopened or moved backwards.</p>
              {["new", "preparing"].includes(viewing.status) && viewing.source === "table" && <div className="mt-5 rounded-lg border border-line p-3"><div className="text-xs uppercase tracking-widest text-ink-muted mb-2">Edit order items</div><div className="flex flex-col sm:flex-row gap-2"><select value={additionId} onChange={(event) => { setAdditionId(event.target.value); setAdditionSizeId(""); }} className="input-field" data-testid="add-order-item-select"><option value="">Choose menu item</option>{(vendor.items || []).filter((item) => item.state === "available" || (item.available !== false && item.isAvailable !== false)).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>{(() => { const item = (vendor.items || []).find(({ id }) => id === additionId); return item?.sizes?.length ? <select value={additionSizeId} onChange={(event) => setAdditionSizeId(event.target.value)} className="input-field" aria-label="Portion" data-testid="add-order-item-size-select"><option value="">Choose portion</option>{item.sizes.filter((size) => size.isAvailable !== false).map((size) => <option key={size.id} value={size.id}>{size.label} · {formatINR(size.price)}</option>)}</select> : null; })()}<button onClick={stageItemForViewing} className="btn-ghost min-h-[44px]" data-testid="add-order-item-button">Add to draft</button></div>{draftItems.length > 0 && <div className="mt-3"><ul className="text-sm space-y-1" data-testid="order-item-draft">{draftItems.map((item) => <li key={item.lineId || item.id}>{item.name}{item.sizeLabel && <span className="capitalize text-ink-secondary"> · {item.sizeLabel}</span>} ×{item.qty}</li>)}</ul><button onClick={saveDraftItems} disabled={savingDraft} className="btn-primary min-h-[44px] mt-3" data-testid="save-order-changes">{savingDraft ? "Saving…" : "Save changes"}</button></div>}</div>}
              <div className="mt-4 flex flex-wrap gap-2">
                {viewing.source === "table" && ["completed", "cancelled"].includes(viewing.status) && !viewing.tableClosedAt && <button onClick={completeViewingTable} className="btn-primary min-h-[44px]" data-testid="complete-table-button">Complete table</button>}
              </div>
            </div>
          </div>
        )}
      </Modal>
      </>}
    </div>
  );
}

const MOBILE_STATUS_TONES = {
  new: "border-l-emerald-400 border-emerald-400/20 bg-emerald-400/[0.035]",
  preparing: "border-l-amber-400 border-amber-400/20 bg-amber-400/[0.035]",
  ready: "border-l-emerald-400 border-emerald-400/20 bg-emerald-400/[0.035]",
  completed: "border-l-violet-400 border-violet-400/20 bg-violet-400/[0.035]",
  cancelled: "border-l-red-400 border-red-400/20 bg-red-400/[0.035]",
};

function shortTime(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Time unavailable" : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function itemSummary(items = []) {
  const visible = items.slice(0, 2).map((item) => `${item.name} ×${item.qty}`).join(", ");
  return items.length > 2 ? `${visible} +${items.length - 2} more` : visible || "No items";
}

function MobileOrderCard({ order, onView }) {
  const context = order.source === "table" ? (order.tableLabel || "Table unavailable") : order.source === "counter" ? "Counter order" : "Online order";
  return (
    <li
      className={`rounded-xl border border-l-4 p-3.5 shadow-sm ${MOBILE_STATUS_TONES[order.status] || "border-l-line border-line bg-bg-elevated"}`}
      data-testid={`order-mobile-card-${order.id}`}
      data-status={order.status}
    >
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-2 items-start">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="font-mono text-sm font-medium truncate">{order.code}</span>
            <span className="text-[11px] text-ink-muted shrink-0">{shortTime(order.createdAt)}</span>
          </div>
          <div className="mt-0.5 text-xs font-medium truncate" data-testid={`mobile-order-context-${order.id}`}>{context}</div>
        </div>
        <StatusBadge status={order.status} />
        <div className="min-w-0">
          <div className="text-sm truncate">{order.customer?.name || "Guest"}</div>
          <div className="mt-0.5 text-xs text-ink-secondary truncate" data-testid={`mobile-order-items-${order.id}`}>{itemSummary(order.items)}</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-sm font-semibold">{formatINR(order.total)}</div>
          <button onClick={onView} className="btn-ghost min-h-[44px] !px-2.5 !py-1 mt-1 inline-flex items-center gap-1 text-xs" data-testid={`mobile-view-order-${order.id}`} aria-label={`View order ${order.code}`}><Eye className="h-3.5 w-3.5" /> View</button>
        </div>
      </div>
    </li>
  );
}

function RequestsList({ requests, onTransition }) {
  return <ServiceRequestsPanel requests={requests} onTransition={onTransition} />;
}

function TableGrid({ vendor, tables, orders, onOpen }) {
  const [qrTable, setQrTable] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const styles = { free: "border-emerald-500/40 bg-emerald-500/5", occupied: "border-amber-500/50 bg-amber-500/5", ready_to_close: "border-emerald-500/50 bg-emerald-500/5" };
  return <><div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="table-grid">{tables.map((table) => { const detail = getTableState(table, orders); return <article key={table.id} className={`card-surface border-2 min-h-[150px] p-4 ${styles[detail.state]}`} data-testid={`table-state-${detail.state}`}><button onClick={() => setSelectedTable(table)} className="w-full min-h-[80px] text-left focus-visible:ring-2 focus-visible:ring-brand" data-testid={`table-card-${table.id}`}><div className="font-display text-lg">{table.label}</div><div className="mt-2 text-sm font-semibold capitalize">{detail.state === "free" ? "✓ Free" : detail.state === "occupied" ? "● Occupied" : "! Ready to close"}</div>{detail.order && <><div className="mt-2 text-xs font-mono">{detail.order.code}</div><div className="text-xs">Order status: <span className="capitalize">{detail.order.status}</span></div></>}</button><button className="btn-ghost min-h-[44px] mt-2 text-xs" onClick={() => setQrTable(table)} data-testid={`table-qr-${table.id}`}>Table QR</button></article>; })}</div><TableHistoryModal table={selectedTable} orders={orders} onClose={() => setSelectedTable(null)} onOpen={(order) => { setSelectedTable(null); onOpen(order); }} /><TableQrModal vendor={vendor} table={qrTable} onClose={() => setQrTable(null)} /></>;
}

function TableHistoryModal({ table, orders, onClose, onOpen }) {
  const history = table ? orders.filter((order) => order.tableId === table.id).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) : [];
  return <Modal open={!!table} onClose={onClose}>{table && <div data-testid="table-detail"><h2 className="font-display text-2xl">{table.label}</h2><p className="mt-1 text-sm text-ink-secondary">Current and previous orders for this table.</p>{history.length === 0 ? <p className="mt-6 text-sm" data-testid="table-no-orders">No orders for this table yet.</p> : <ul className="mt-5 divide-y divide-line">{history.map((order) => <li key={order.id} className="py-3"><button className="w-full min-h-[44px] text-left" onClick={() => onOpen(order)} data-testid={`table-order-${order.id}`}><div className="flex justify-between gap-3"><span className="font-mono">{order.code}</span><span className="font-mono">{formatINR(order.total)}</span></div><div className="mt-1 text-xs"><span className="capitalize">{order.status}</span> · {formatDateTime(order.createdAt)}</div></button></li>)}</ul>}</div>}</Modal>;
}

function TableQrModal({ vendor, table, onClose }) {
  const url = table ? buildTableUrl(window.location.origin, vendor, table.id) : null;
  const [image, setImage] = useState(null);
  React.useEffect(() => { let active = true; setImage(null); if (url) createQrSvgDataUrl(url).then((value) => active && setImage(value)).catch(() => active && toast.error("Could not generate this table QR. Try again.")); return () => { active = false; }; }, [url]);
  const download = () => { if (!image) return toast.error("Wait for the QR preview to finish"); const anchor = document.createElement("a"); anchor.href = image; anchor.download = `${vendor.slug}-${table.id}.png`; anchor.click(); };
  const print = () => { if (!image) return toast.error("Wait for the QR preview to finish"); const popup = window.open("", "_blank"); if (!popup) return toast.error("Allow pop-ups to print this table QR"); popup.document.write(`<title>${vendor.name} ${table.label}</title><h1>${vendor.name}</h1><h2>${table.label}</h2><img alt="${table.label} QR" src="${image}"><p>${url}</p><footer><img alt="" width="24" height="24" src="${APP_CONFIG.brandAssets.icon192}"> Powered by ${APP_CONFIG.brandLabel}</footer>`); popup.document.close(); popup.print(); };
  return <Modal open={!!table} onClose={onClose}>{table && <div data-testid="table-qr-preview"><h2 className="font-display text-2xl">{vendor.name}</h2><p className="mt-1 font-medium">{table.label}</p>{image ? <img className="mt-4 w-56 h-56 bg-white" src={image} alt={`QR for ${vendor.name} ${table.label}`} /> : <p role="status" className="mt-4">Generating QR…</p>}<p className="mt-3 text-xs font-mono break-all">{url || "This table QR is unavailable."}</p><div className="mt-4 flex gap-2"><button className="btn-ghost min-h-[44px]" onClick={download} data-testid="table-qr-download">Download PNG</button><button className="btn-primary min-h-[44px]" onClick={print} data-testid="table-qr-print">Print</button></div></div>}</Modal>;
}

function InfoBlock({ label, children, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[11px] uppercase tracking-widest text-ink-muted mb-1">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}
function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "text-ink-primary font-medium" : "text-ink-secondary"}>{label}</span>
      <span className={`font-mono ${bold ? "text-lg font-semibold" : ""}`}>{value}</span>
    </div>
  );
}
